import { AnyAri } from '@atlassian/ari/any-ari';
import { type ContentState } from '@databases/types/confluence';
import { getContentById } from '@databases/utils/confluence/content';
import {
  CONTENT_EXPANSIONS,
  addContentToList,
} from '@databases/utils/redux/content/actions';
import { selectContentByIdOptional } from '@databases/utils/redux/content/selectors';
import contentSlice from '@databases/utils/redux/content/slice';
import { loadAsync } from '@databases/utils/redux/redux';
import { type AnyAction } from 'redux';
import { type ThunkAction } from 'redux-thunk';

import { type DefaultRootState } from '../../redux';
import {
  type ConnectionContentWithColumns,
  selectContentColumnStatus,
  selectContentOptional,
} from '../selectors';
import connectionsSlice, { type Content } from '../slice';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

export const loadContentPageStatus = ({
  ari,
  forceReload,
}: {
  ari: string;
  forceReload?: boolean;
}): Dispatcher<Promise<ConnectionContentWithColumns | undefined>> => {
  return async (dispatch, getState) => {
    const content: Content | undefined = getState().connections.content[ari];
    if (!content) {
      return undefined;
    }

    await loadAsync(
      () => selectContentColumnStatus(getState().connections, ari, 'pageStatus'),
      () => {
        dispatch(
          connectionsSlice.actions.loadContentColumnPending({
            ari,
            column: 'pageStatus',
          })
        );
      },
      async () => {
        const content = selectContentOptional(getState().connections, ari);
        if (!content) {
          throw new Error('Content not found');
        }

        const contentId = AnyAri.parse(ari).resourceId;
        let contentData = selectContentByIdOptional(getState(), { contentId });
        if (!contentData) {
          contentData = await getContentById(contentId, CONTENT_EXPANSIONS);
          if (contentData) {
            dispatch(addContentToList(contentData));
          }
          dispatch(contentSlice.actions.loadContentByIdFinished({ contentId }));
        }

        if (!contentData) {
          return undefined;
        }

        const contentStateValue =
          contentData?.metadata?.properties?.['content-state-published']?.value;
        const parsedContentStateValue =
          contentStateValue && JSON.parse(contentStateValue);

        return parsedContentStateValue?.contentState as ContentState | undefined;
      },
      (contentState) => {
        if (contentState) {
          dispatch(
            connectionsSlice.actions.addColumnValueToContent({
              ari,
              columnName: 'pageStatus',
              data: {
                type: 'pageStatus',
                text: contentState.name,
                value: {
                  text: contentState.name,
                  color: contentState.color,
                },
              },
            })
          );
        }
        dispatch(
          connectionsSlice.actions.loadContentColumnFinished({
            ari,
            column: 'pageStatus',
          })
        );
      },
      (error) => {
        dispatch(
          connectionsSlice.actions.loadContentColumnError({
            ari,
            column: 'pageStatus',
            error,
          })
        );
      },
      { forceReload }
    );

    return selectContentOptional(getState().connections, ari);
  };
};
